<template>
  <div id="accountBank">
    <div v-if="loaded">
      <b-card no-body class="border mt-1 building-properties-card">
        <b-table
            ref="propertyDetails-table"
            class="building-properties-table"
            hover
            small
            responsive
            :items="cptItems"
            :fields="fields"
            :busy="!loaded"
            primary-key="id"
            empty-text="—"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mx-1"></b-spinner>
              <strong>{{ msg('Loading') }}...</strong>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge v-if="data.item.buildingId == null" variant="light-danger" pill class="px-1">
              <feather-icon icon="AlertTriangleIcon" size="11" class="mr-25"/>
              {{ msg('Unlinked') }}
            </b-badge>
            <b-badge v-else variant="light-success" pill class="px-1">
              <feather-icon icon="CheckCircleIcon" size="11" class="mr-25"/>
              {{ msg('Linked') }}
            </b-badge>
          </template>

          <template #cell(address)="data">
            <div>
              <div class="font-weight-bold">{{ getFullAddress(data.item) || '—' }}</div>
              <small class="text-muted">
                <feather-icon icon="MapPinIcon" size="11" class="mr-25"/>
                <strong>{{ msg('Zip') }}:</strong> {{ data.item.zip || '—' }}
              </small>
            </div>
          </template>

          <template #cell(realEstateTreasure.keyCode)="data">
            <div v-if="data.value">
              <span v-if="!showFullText">{{ String(data.value).substring(0, maxLength) }}<span v-if="String(data.value).length > maxLength">…</span></span>
              <span v-else>{{ data.value }}</span>
              <b-link v-if="!showFullText && String(data.value).length > maxLength" @click="showText(data.item)">{{ msg('Read more') }}</b-link>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(realEstateArea.totalSpace)="data">
            <span :class="data.value > 0 ? '' : 'text-muted'">
              {{ data.value > 0 ? data.value + ' m²' : '—' }}
            </span>
          </template>

          <template #cell(realEstateArea.rooms)="data">
            <span :class="data.value > 0 ? '' : 'text-muted'">
              {{ data.value > 0 ? data.value : '—' }}
            </span>
          </template>

          <template #cell(actions)="data">
            <div class="action-cell">
              <b-button
                  size="sm"
                  variant="outline-primary"
                  class="action-btn"
                  @click="goToRelaEstate(data.item)">
                <feather-icon icon="ExternalLinkIcon" size="13" class="mr-25"/>
                {{ msg('Open') }}
              </b-button>
              <b-button
                  size="sm"
                  variant="outline-danger"
                  class="action-btn"
                  :disabled="removingId === data.item.id"
                  @click="info(data.item, data.index, $event.target)">
                <b-spinner v-if="removingId === data.item.id" small class="mr-25"/>
                <feather-icon v-else icon="Trash2Icon" size="13" class="mr-25"/>
                {{ msg('Remove') }}
              </b-button>
            </div>
          </template>
        </b-table>

        <div v-if="cptItems.length === 0" class="empty-state text-center text-muted py-3">
          <feather-icon icon="HomeIcon" size="24" class="mb-50 d-block mx-auto"/>
          {{ msg('No properties linked to this building yet') }}
        </div>
      </b-card>

      <b-modal v-model="showPopup" :title="msg('Read more')">
        <div>{{ selectedText }}</div>
        <template #modal-footer>
          <b-button variant="primary" @click="showPopup = false">{{ msg('close') }}</b-button>
        </template>
      </b-modal>

      <b-modal :id="infoModal.id" :title="msg('Confirmation')"
               @ok="archiveRealEstateById(infoModal.propertyId)"
               ok-variant="danger" :ok-title="msg('Remove')" :cancel-title="msg('cancel')"
               @hide="resetInfoModal">
        <p class="my-2">{{ msg('areyousureyouwanttotakethisrealestateout') }}</p>
      </b-modal>
    </div>
    <div v-else-if="this.error != ''">
      <b-alert show variant="danger"><i class="fas fa-exclamation-triangle mr-3"></i>
        <strong>{{ error }}</strong>
      </b-alert>
    </div>
    <div v-else>
      <b-spinner label="Spinning"/>
      {{ msg('loading') }}
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'buildingProperties',
  components: {},
  props: {
    value: {
      type: Object,
      required: true,
    },
    historyMaxHeight: {
      default: 500,
      type: Number
    },
    disableAddBank: {
      required: false,
      type: Boolean
    }
  },
  watch: {
    /**
     * Only react to a real change in the linked property IDs — the previous
     * `deep: true` watcher fired on every nested mutation (e.g. when this
     * component itself decorated rows), causing duplicate concurrent fetches
     * that produced ghost rows on refresh.
     */
    'value.propertyIds': {
      handler() {
        this.loadProperties()
      },
      deep: false
    },
    'value.id': {
      handler(newId, oldId) {
        if (newId !== oldId) this.loadProperties()
      }
    }
  },
  data: () => ({
    loaded: false,
    infoModal: {
      id: 'info2-modal',
      title: '',
      content: '',
      propertyId: null,
    },
    selectedText: null,
    showFullText: false,
    maxLength: 50,
    showPopup: false,
    error: '',
    removingId: null,
    /** Monotonic counter — every load() call gets a new token; only the
     *  latest in-flight request is allowed to write back into propertyList,
     *  which prevents stale responses from injecting ghost rows. */
    _loadToken: 0,
    propertyList: [],
  }),
  computed: {
    ...mapGetters('translator', ['msg', 'currentFormattedLang']),
    ...mapGetters('data', ['getUserFullName', 'getUsersByRole', 'getAllUsers', 'getUser']),
    ...mapGetters('building', ['fileTags']),
    ...mapGetters('realEstate', ["getFullAddress"]),

    building: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    /**
     * Returns row objects WITHOUT mutating the underlying property list.
     * Mutating the items in-place (the previous behavior) re-triggered a
     * deep `value` watcher because the rows are part of the same object
     * graph, which kicked off a fresh load mid-flight and produced
     * duplicates on consecutive refreshes.
     */
    cptItems() {
      return this.propertyList.map(item => ({
        ...item,
        _rowVariant: item.buildingId == null ? 'danger' : ''
      }))
    },

    fields() {
      const $this = this
      return [
        {key: 'status', label: $this.msg('Status'), sortable: false, thStyle: {width: '110px'}},
        {key: 'address', label: $this.msg('Address'), sortable: true},
        {key: 'realEstateArea.totalSpace', label: $this.msg('Space'), sortable: true, thStyle: {width: '100px'}},
        {key: 'realEstateArea.rooms', label: $this.msg('rooms'), sortable: true, thStyle: {width: '90px'}},
        {key: 'realEstateTreasure.keyCode', label: $this.msg('CheckIn details'), sortable: false},
        {key: 'actions', label: $this.msg('Action'), sortable: false, thClass: 'text-right', tdClass: 'text-right', thStyle: {width: '210px'}},
      ]
    },
  },
  created() {
    this.loadProperties()
  },
  methods: {
    ...mapActions("document", ["downloadCustomFile"]),
    ...mapActions('building', ['setBankToBuilding', 'updateBankDocumentFile', 'removeProperty', 'addProperty']),
    ...mapActions('realEstate', {_loadPropertiesList: 'getAllForBuildings'}),

    loadProperties() {
      const ids = (this.building && Array.isArray(this.building.propertyIds))
          ? this.building.propertyIds.filter(id => id != null && id !== '')
          : []

      // Empty list → clear immediately, no API roundtrip needed.
      if (ids.length === 0) {
        this.propertyList = []
        this.loaded = true
        return
      }

      // Build a fresh filter object every call — sharing a long-lived filter
      // (the previous behavior) leaked state between successive loads.
      const filter = {
        like: {search: '', name: '', shortDescription: ''},
        eq: {type: '', realEstateArea: {}, featureStatus: null},
        lte: {realEstatePrices: {}},
        gte: {realEstatePrices: {}},
        in: {id: ids},
      }

      const token = ++this._loadToken
      this._loadPropertiesList({amount: 9999, filter}).then(ans => {
        // Drop responses that lost the race against a newer load().
        if (token !== this._loadToken) return

        const list = (ans && ans['RealEstates']) || []

        // Defensive de-duplication by id — guards against the backend
        // returning the same property twice (which is what produced
        // “suddenly more properties appear” after multiple refreshes).
        const seen = new Set()
        this.propertyList = list.filter(p => {
          if (!p || p.id == null) return false
          if (seen.has(p.id)) return false
          seen.add(p.id)
          return true
        })
      }).finally(() => {
        if (token === this._loadToken) this.loaded = true
      })
    },

    showText(item) {
      this.selectedText = item.realEstateTreasure ? item.realEstateTreasure.keyCode : ''
      this.showPopup = true
    },

    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.propertyId = item.id
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },

    goToRelaEstate(item) {
      this.$router.push(`/real_estate/edit/${item.id}`)
    },

    resetInfoModal() {
      this.infoModal.propertyId = null
      this.$root.$emit('bv::hide::modal', this.infoModal.id)
    },

    archiveRealEstateById(realId) {
      if (realId == null) return
      this.removingId = realId

      // Optimistic remove from the visible table — the server roundtrip
      // below confirms it. We DO NOT mutate `building.propertyIds` here:
      // - `splice(idx)` (without count) was deleting everything from the
      //   index onwards, which is what made delete look broken.
      // - Mutating the prop in place would also re-trigger the watcher and
      //   race with the API response.
      const previous = this.propertyList
      this.propertyList = previous.filter(p => p.id !== realId)

      this.removeProperty({buildingId: this.building.id, propertyId: realId})
          .then(ans => {
            // Server returned the canonical building — emit it up so the
            // parent's `value` is consistent. Our `value.propertyIds`
            // watcher will reload and reconcile.
            if (ans) this.building = ans
            this.resetInfoModal()
            this.$emit('delete', realId)
          })
          .catch(err => {
            // Roll back the optimistic update.
            this.propertyList = previous
            this.error = (err && err.message) || String(err)
            this.resetInfoModal()
          })
          .finally(() => {
            this.removingId = null
          })
    },

    onValueUpdated(args) {
      this.$emit('change', args)
    },

    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber)
    },
  }
}
</script>

<style scoped>
.building-properties-card {
  overflow: hidden;
}

.building-properties-table ::v-deep th {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.025);
}

.dark-layout .building-properties-table ::v-deep th {
  background: rgba(255, 255, 255, 0.04);
}

.building-properties-table ::v-deep td {
  vertical-align: middle;
  padding: 0.6rem 0.75rem;
}

.action-cell {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
}

.action-btn {
  white-space: nowrap;
  padding: 0.35rem 0.7rem;
}

.empty-state {
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
}

.dark-layout .empty-state {
  border-top-color: rgba(255, 255, 255, 0.08);
}
</style>
